import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ChevronDown, Plus, Minus } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "@/constants/assets";
import { search as searchSite } from "@/lib/search";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showBanner = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActive, setSearchActive] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null); // desktop hover
  const [openMobileGroup, setOpenMobileGroup] = useState(null);

  const searchResults = useMemo(
    () => searchSite(searchQuery, 8),
    [searchQuery],
  );

  // Reset results cursor whenever the query changes
  useEffect(() => {
    setSearchActive(0);
  }, [searchQuery]);

  // Reset the whole search state when the overlay closes
  useEffect(() => {
    if (!searchOpen) {
      setSearchQuery("");
      setSearchActive(0);
    }
  }, [searchOpen]);

  // Close the overlay whenever the route changes (click-through navigation)
  useEffect(() => {
    setSearchOpen(false);
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname, location.search, location.hash]);

  const goToResult = (r) => {
    if (!r) return;
    setSearchOpen(false);
    // Hash link = jump to anchor on home page
    if (r.href.startsWith("/#")) {
      navigate("/");
      // let the route transition settle before scrolling
      setTimeout(() => {
        const id = r.href.slice(2);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } else {
      navigate(r.href);
    }
  };

  const onSearchKeyDown = (e) => {
    if (!searchResults.length) {
      if (e.key === "Escape") setSearchOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchActive((i) => (i + 1) % searchResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchActive(
        (i) => (i - 1 + searchResults.length) % searchResults.length,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      goToResult(searchResults[searchActive]);
    } else if (e.key === "Escape") {
      setSearchOpen(false);
    }
  };

  useEffect(() => {
    // Auto-hide on scroll down, reveal on scroll up. Uses rAF-throttled read
    // to keep it silky against Lenis' smooth scroll. Header always shows
    // near the very top so the hero isn't ever without a header at load.
    let lastY = window.scrollY;
    let ticking = false;
    const REVEAL_THRESHOLD = 96; // px past which we allow hiding
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 40);
        if (y <= REVEAL_THRESHOLD) {
          setHidden(false);
        } else if (y > lastY + 4) {
          setHidden(true);
        } else if (y < lastY - 4) {
          setHidden(false);
        }
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Any menu/search/dropdown open state should force the header visible so
  // interactions don't disappear mid-flow.
  useEffect(() => {
    if (menuOpen || searchOpen || openDropdown) setHidden(false);
  }, [menuOpen, searchOpen, openDropdown]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
  }, [menuOpen]);

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: hidden ? -160 : 0, opacity: hidden ? 0 : 1 }}
        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
        className={`fixed inset-x-0 top-0 z-[80] transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-[#0a0a0c]/75 backdrop-blur-xl"
            : "border-b border-transparent bg-gradient-to-b from-black/70 to-transparent"
        }`}
        onMouseLeave={() => setOpenDropdown(null)}
      >
        {/* ── Showroom announcement pill (homepage only) ─────────── */}
        {showBanner && (
          <div className="pointer-events-none flex justify-center px-4 pt-3 sm:pt-4">
            <Link
              to="/showroom"
              data-testid="showroom-banner"
              className="pointer-events-auto group inline-flex max-w-[92%] items-center justify-center gap-2 rounded-full border border-[#c6a87c]/40 bg-[#0a0a0c]/90 px-4 py-1.5 backdrop-blur-md transition-all duration-300 hover:border-[#c6a87c] hover:bg-[#c6a87c]/15 hover:shadow-[0_0_0_2px_rgba(198,168,124,0.15)] focus:outline-none focus:ring-2 focus:ring-[#c6a87c]/60 sm:gap-3 sm:px-6 sm:py-2"
            >
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full bg-[#c6a87c] transition-transform duration-300 group-hover:scale-125"
              />
              <span
                data-testid="showroom-banner-text"
                className="font-tech text-[10px] font-semibold uppercase leading-tight tracking-[0.24em] text-white/85 transition-colors duration-300 group-hover:text-[#c6a87c] sm:text-[11px] sm:tracking-[0.28em]"
              >
                Visit our By Appointment Auckland Showroom
              </span>
              <span
                aria-hidden
                className="font-tech text-[11px] text-[#c6a87c]/70 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-[#c6a87c]"
              >
                →
              </span>
            </Link>
          </div>
        )}

        {/* ── Single-row header: Logo + nav + actions ─────────────── */}
        <div className="mx-auto flex h-20 max-w-[1760px] items-center justify-between gap-6 px-6 lg:h-24 lg:gap-8 lg:px-12">
          {/* Wordmark */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="group relative shrink-0 font-body text-[22px] font-light uppercase tracking-[0.42em] text-[#e6c893] transition-colors duration-300 hover:text-[#f2d9a8] sm:text-[24px] lg:text-[26px]"
            style={{
              textShadow:
                "0 0 18px rgba(198,168,124,0.35), 0 1px 0 rgba(0,0,0,0.55)",
            }}
          >
            <span className="relative inline-block">
              EUROSTEEL
              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-gradient-to-r from-[#c6a87c] via-[#e6c893] to-transparent transition-transform duration-500 ease-out group-hover:scale-x-100"
              />
            </span>
          </Link>

          {/* Desktop nav — inline with logo */}
          <nav
            data-testid="desktop-nav"
            className="hidden flex-1 items-center justify-center gap-5 xl:flex xl:gap-6 2xl:gap-8"
          >
            {NAV_ITEMS.map((item) => {
              const hasChildren = !!item.children?.length;
              const isOpen = openDropdown === item.label;
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() =>
                    hasChildren ? setOpenDropdown(item.label) : setOpenDropdown(null)
                  }
                >
                  <Link
                    to={item.href || `/${slug(item.label)}`}
                    data-testid={`nav-link-${slug(item.label)}`}
                    className="nav-underline inline-flex items-center gap-1 whitespace-nowrap font-body text-[10px] font-medium uppercase tracking-[0.14em] text-white/75 transition-colors duration-300 hover:text-white 2xl:text-[11px]"
                  >
                    {item.label}
                    {hasChildren && (
                      <ChevronDown
                        className={`h-3 w-3 transition-transform duration-300 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        strokeWidth={1.6}
                      />
                    )}
                  </Link>

                  {hasChildren && (
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          data-testid={`dropdown-${slug(item.label)}`}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 6 }}
                          transition={{ duration: 0.28, ease: [0.19, 1, 0.22, 1] }}
                          className="absolute left-1/2 top-full z-10 mt-5 min-w-[240px] -translate-x-1/2"
                        >
                          <div className="rounded-md border border-white/10 bg-[#0a0a0c]/95 p-3 shadow-2xl backdrop-blur-xl">
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.href}
                                data-testid={`dropdown-item-${slug(child.label)}`}
                                onClick={() => setOpenDropdown(null)}
                                className="group flex items-center justify-between rounded-sm px-4 py-3 font-body text-[11px] uppercase tracking-[0.16em] text-white/70 transition-colors duration-300 hover:bg-white/[0.04] hover:text-[#c6a87c]"
                              >
                                <span>{child.label}</span>
                                <span className="translate-x-[-4px] text-[#c6a87c] opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100">
                                  →
                                </span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex shrink-0 items-center gap-3 lg:gap-4">
            <button
              data-testid="search-button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 text-white/80 transition-all duration-300 hover:border-[#c6a87c]/60 hover:text-[#c6a87c]"
            >
              <Search className="h-[17px] w-[17px]" strokeWidth={1.5} />
            </button>

            <a
              href="mailto:isaiah@euro-steel.co.nz?subject=Eurosteel%20Enquiry"
              data-testid="enquire-button"
              className="hidden whitespace-nowrap rounded-full bg-[#c6a87c] px-8 py-3 font-tech text-[11px] uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93] sm:block"
            >
              Enquire
            </a>

            {/* Hamburger (mobile / tablet) */}
            <button
              data-testid="menu-toggle"
              aria-label="Menu"
              onClick={() => setMenuOpen(true)}
              className="flex h-6 w-7 flex-col items-end justify-center gap-[6px] xl:hidden"
            >
              <span className="h-px w-7 bg-white" />
              <span className="h-px w-5 bg-white" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Fullscreen mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
            className="fixed inset-0 z-[95] flex flex-col bg-[#0a0a0c]"
          >
            <div className="grain absolute inset-0" />
            <div className="relative flex h-20 items-center justify-between px-6 lg:px-10">
              <span className="font-body text-xl font-light uppercase tracking-[0.5em] text-[#c6a87c]">
                EUROSTEEL
              </span>
              <button
                data-testid="menu-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="text-white/80 transition-colors hover:text-[#c6a87c]"
              >
                <X className="h-7 w-7" strokeWidth={1.2} />
              </button>
            </div>

            <nav className="relative flex flex-1 flex-col justify-center gap-1 overflow-y-auto px-6 pb-8 lg:px-10">
              {NAV_ITEMS.map((item, i) => {
                const hasChildren = !!item.children?.length;
                const expanded = openMobileGroup === item.label;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 0.15 + i * 0.04,
                      duration: 0.55,
                      ease: [0.19, 1, 0.22, 1],
                    }}
                    className="border-b border-white/5"
                  >
                    {hasChildren ? (
                      <>
                        <button
                          data-testid={`mobile-group-${slug(item.label)}`}
                          onClick={() =>
                            setOpenMobileGroup(expanded ? null : item.label)
                          }
                          className="flex w-full items-center justify-between py-3 font-serif-display text-[6.5vw] leading-[1.15] text-white/85 transition-colors duration-300 hover:text-[#c6a87c] sm:text-3xl"
                        >
                          <span>{item.label}</span>
                          {expanded ? (
                            <Minus className="h-6 w-6 text-[#c6a87c]" strokeWidth={1.2} />
                          ) : (
                            <Plus className="h-6 w-6 text-white/60" strokeWidth={1.2} />
                          )}
                        </button>
                        <AnimatePresence>
                          {expanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
                              className="overflow-hidden"
                            >
                              <div className="flex flex-col gap-2 pb-4 pl-2">
                                {item.children.map((child) => (
                                  <Link
                                    key={child.label}
                                    to={child.href}
                                    data-testid={`mobile-sublink-${slug(child.label)}`}
                                    onClick={() => setMenuOpen(false)}
                                    className="font-body text-[13px] uppercase tracking-[0.18em] text-white/60 transition-colors duration-300 hover:text-[#c6a87c]"
                                  >
                                    {child.label}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={item.href || `/${slug(item.label)}`}
                        data-testid={`mobile-link-${slug(item.label)}`}
                        onClick={() => setMenuOpen(false)}
                        className="block py-3 font-serif-display text-[6.5vw] leading-[1.15] text-white/85 transition-colors duration-300 hover:text-[#c6a87c] sm:text-3xl"
                      >
                        {item.label}
                      </Link>
                    )}
                  </motion.div>
                );
              })}
            </nav>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="relative px-6 pb-10 lg:px-10"
            >
              <a
                href="mailto:isaiah@euro-steel.co.nz?subject=Eurosteel%20Enquiry"
                data-testid="mobile-enquire-button"
                onClick={() => setMenuOpen(false)}
                className="block w-full rounded-full bg-[#c6a87c] py-4 text-center font-tech text-xs uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93]"
              >
                Enquire
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            data-testid="search-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[96] flex items-start justify-center bg-[#0a0a0c]/95 backdrop-blur-lg"
          >
            <button
              data-testid="search-close"
              onClick={() => setSearchOpen(false)}
              className="absolute right-6 top-7 text-white/70 transition-colors hover:text-[#c6a87c] lg:right-10"
              aria-label="Close search"
            >
              <X className="h-7 w-7" strokeWidth={1.2} />
            </button>
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
              className="mt-[18vh] w-full max-w-3xl px-6"
            >
              <p className="font-tech mb-6 text-[11px] uppercase tracking-[0.4em] text-[#c6a87c]">
                Search
              </p>
              <input
                data-testid="search-input"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search products, projects, technical..."
                className="w-full border-b border-white/20 bg-transparent pb-5 font-serif-display text-3xl text-white placeholder:text-white/30 focus:border-[#c6a87c] focus:outline-none sm:text-5xl"
              />

              {/* Results */}
              {searchQuery.trim() && (
                <div
                  data-testid="search-results"
                  className="mt-6 max-h-[55vh] overflow-y-auto"
                >
                  {searchResults.length === 0 ? (
                    <p
                      data-testid="search-no-results"
                      className="font-body text-[13px] italic text-white/55"
                    >
                      No results for{" "}
                      <span className="text-white/80">"{searchQuery}"</span>.
                      Try "steel doors", "SD3", "pivot", or "showroom".
                    </p>
                  ) : (
                    <ul className="divide-y divide-white/10">
                      {searchResults.map((r, i) => {
                        const active = i === searchActive;
                        return (
                          <li key={`${r.href}-${r.title}-${i}`}>
                            <button
                              type="button"
                              data-testid={`search-result-${i}`}
                              onMouseEnter={() => setSearchActive(i)}
                              onClick={() => goToResult(r)}
                              className={`group flex w-full items-start justify-between gap-6 px-1 py-3.5 text-left transition-colors duration-200 ${
                                active ? "text-white" : "text-white/70"
                              }`}
                            >
                              <div className="min-w-0 flex-1">
                                <p
                                  className={`font-serif-display text-[17px] leading-tight sm:text-[19px] ${
                                    active ? "text-[#c6a87c]" : "text-white"
                                  } transition-colors duration-200`}
                                >
                                  {r.title}
                                </p>
                                {r.subtitle && (
                                  <p className="mt-1 truncate font-body text-[12px] text-white/55 sm:text-[13px]">
                                    {r.subtitle}
                                  </p>
                                )}
                              </div>
                              <span
                                className={`shrink-0 font-tech text-[10px] font-semibold uppercase tracking-[0.26em] ${
                                  active ? "text-[#c6a87c]" : "text-white/40"
                                } transition-colors duration-200`}
                              >
                                {r.kind}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              {!searchQuery.trim() && (
                <p className="mt-6 font-tech text-[10px] uppercase tracking-[0.3em] text-white/35">
                  Try steel doors · SD3 · pivot entrance · finishes · showroom
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
